import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import WalletOverview from './components/WalletOverview';
import CurrencyCard from './components/CurrencyCard';
import DepositModal from './components/DepositModal';
import WithdrawModal from './components/WithdrawModal';
import TransactionHistory from './components/TransactionHistory';
import SecurityPrompt from './components/SecurityPrompt';

const WalletTransactions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isSecurityPromptOpen, setIsSecurityPromptOpen] = useState(false);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('30days');

  // Mock wallet data
  const walletData = {
    totalBalance: 125847.32,
    totalBalanceChange: 2.45,
    availableBalance: 118234.56,
    pendingBalance: 7612.76,
    currencies: [
      {
        id: 'btc',
        symbol: 'BTC',
        name: 'Bitcoin',
        balance: 2.45678901,
        usdValue: 67890.12,
        change24h: 3.45,
        icon: 'https://cryptologos.cc/logos/bitcoin-btc-logo.png',
        network: 'Bitcoin',
        depositAddress: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
        minWithdrawal: 0.001,
        withdrawalFee: 0.0005,
        confirmations: 6
      },
      {
        id: 'eth',
        symbol: 'ETH',
        name: 'Ethereum',
        balance: 15.23456789,
        usdValue: 28456.78,
        change24h: -1.23,
        icon: 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
        network: 'Ethereum',
        depositAddress: '0x742d35Cc6634C0532925a3b8D4C2C2e4C8b4c4c4',
        minWithdrawal: 0.01,
        withdrawalFee: 0.005,
        confirmations: 12
      },
      {
        id: 'usdt',
        symbol: 'USDT',
        name: 'Tether USD',
        balance: 25000.00,
        usdValue: 25000.00,
        change24h: 0.01,
        icon: 'https://cryptologos.cc/logos/tether-usdt-logo.png',
        network: 'Ethereum',
        depositAddress: '0x742d35Cc6634C0532925a3b8D4C2C2e4C8b4c4c4',
        minWithdrawal: 10,
        withdrawalFee: 5,
        confirmations: 12
      },
      {
        id: 'usd',
        symbol: 'USD',
        name: 'US Dollar',
        balance: 5000.00,
        usdValue: 5000.00,
        change24h: 0.00,
        icon: 'https://flagcdn.com/w40/us.png',
        network: 'Bank Transfer',
        isFiat: true,
        minWithdrawal: 10,
        withdrawalFee: 0,
        processingTime: '1-3 business days'
      }
    ]
  };

  const mockTransactions = [
    {
      id: 'tx001',
      type: 'deposit',
      currency: 'BTC',
      amount: 0.5,
      usdValue: 13500.00,
      status: 'completed',
      timestamp: new Date(Date.now() - 3600000),
      txHash: '0x1234567890abcdef1234567890abcdef12345678',
      confirmations: 6,
      network: 'Bitcoin'
    },
    {
      id: 'tx002',
      type: 'withdrawal',
      currency: 'ETH',
      amount: 2.0,
      usdValue: 3800.00,
      status: 'pending',
      timestamp: new Date(Date.now() - 1800000),
      txHash: '0xabcdef1234567890abcdef1234567890abcdef12',
      confirmations: 8,
      network: 'Ethereum',
      address: '0x742d35Cc6634C0532925a3b8D4C2C2e4C8b4c4c4'
    },
    {
      id: 'tx003',
      type: 'transfer',
      currency: 'USDT',
      amount: 1000.00,
      usdValue: 1000.00,
      status: 'completed',
      timestamp: new Date(Date.now() - 7200000),
      description: 'Transfer to Trading Account'
    },
    {
      id: 'tx004',
      type: 'deposit',
      currency: 'USD',
      amount: 2000.00,
      usdValue: 2000.00,
      status: 'processing',
      timestamp: new Date(Date.now() - 86400000),
      description: 'Bank Transfer - ACH'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Wallet' },
    { id: 'deposit', label: 'Deposit', icon: 'ArrowDownToLine' },
    { id: 'withdraw', label: 'Withdraw', icon: 'ArrowUpFromLine' },
    { id: 'history', label: 'Transaction History', icon: 'History' },
    { id: 'security', label: 'Security', icon: 'Shield' }
  ];

  const handleWithdraw = (currency, amount, address) => {
    if (amount > 1000) {
      setPendingWithdrawal({ currency, amount, address });
      setIsSecurityPromptOpen(true);
    } else {
      // Process withdrawal directly
      console.log('Processing withdrawal:', { currency, amount, address });
      setIsWithdrawModalOpen(false);
    }
  };

  const handleSecurityConfirm = (code) => {
    if (code === '123456') {
      console.log('Processing withdrawal with 2FA:', pendingWithdrawal);
      setIsSecurityPromptOpen(false);
      setIsWithdrawModalOpen(false);
      setPendingWithdrawal(null);
    } else {
      return false;
    }
  };

  const handleTransferToTrading = (currency, amount) => {
    console.log('Transferring to trading account:', { currency, amount });
  };

  const filteredTransactions = mockTransactions.filter(tx => {
    const matchesSearch = tx.currency.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tx.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || tx.type === filterType;
    
    let matchesDate = true;
    const now = new Date();
    const txDate = new Date(tx.timestamp);
    
    switch (dateRange) {
      case '7days':
        matchesDate = (now - txDate) <= 7 * 24 * 60 * 60 * 1000;
        break;
      case '30days':
        matchesDate = (now - txDate) <= 30 * 24 * 60 * 60 * 1000;
        break;
      case '90days':
        matchesDate = (now - txDate) <= 90 * 24 * 60 * 60 * 1000;
        break;
    }
    
    return matchesSearch && matchesType && matchesDate;
  });

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 mb-6">
          <button
            onClick={() => navigate('/trading-dashboard')}
            className="text-text-secondary hover:text-primary transition-colors duration-trading-fast"
          >
            Dashboard
          </button>
          <Icon name="ChevronRight" size={16} className="text-text-secondary" />
          <span className="text-text-primary font-body-medium">Wallet & Transactions</span>
        </div>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading-bold text-text-primary mb-2">
              Wallet & Transactions
            </h1>
            <p className="text-text-secondary font-body-normal">
              Manage your cryptocurrency and fiat balances, deposits, and withdrawals
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            <button
              onClick={() => setIsDepositModalOpen(true)}
              className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-trading font-body-medium hover:bg-primary-600 transition-colors duration-trading-fast"
            >
              <Icon name="ArrowDownToLine" size={16} />
              <span>Deposit</span>
            </button>
            <button
              onClick={() => setIsWithdrawModalOpen(true)}
              className="flex items-center space-x-2 bg-secondary-100 text-text-primary px-4 py-2 rounded-trading font-body-medium hover:bg-secondary-200 transition-colors duration-trading-fast"
            >
              <Icon name="ArrowUpFromLine" size={16} />
              <span>Withdraw</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap items-center space-x-1 mb-8 border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 font-body-medium text-sm border-b-2 transition-all duration-trading-fast ${
                activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-200'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {activeTab === 'overview' && (
            <>
              <WalletOverview 
                walletData={walletData}
                onTransferToTrading={handleTransferToTrading}
              />
              
              <div>
                <h2 className="text-xl font-heading-semibold text-text-primary mb-6">
                  Your Currencies
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {walletData.currencies.map((currency) => (
                    <CurrencyCard
                      key={currency.id}
                      currency={currency}
                      onDeposit={() => {
                        setSelectedCurrency(currency);
                        setIsDepositModalOpen(true);
                      }}
                      onWithdraw={() => {
                        setSelectedCurrency(currency);
                        setIsWithdrawModalOpen(true);
                      }}
                      onTransfer={(amount) => handleTransferToTrading(currency.symbol, amount)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === 'deposit' && (
            <div className="bg-surface rounded-trading border border-border p-6">
              <h2 className="text-xl font-heading-semibold text-text-primary mb-6">
                Deposit Funds
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {walletData.currencies.map((currency) => (
                  <button
                    key={currency.id}
                    onClick={() => {
                      setSelectedCurrency(currency);
                      setIsDepositModalOpen(true);
                    }}
                    className="flex items-center space-x-3 p-4 border border-border rounded-trading hover:border-primary hover:bg-primary-50 transition-all duration-trading-fast"
                  >
                    <Image
                      src={currency.icon}
                      alt={currency.name}
                      className="w-8 h-8 rounded-full"
                    />
                    <div className="text-left">
                      <p className="font-body-medium text-text-primary">{currency.symbol}</p>
                      <p className="text-sm text-text-secondary">{currency.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'withdraw' && (
            <div className="bg-surface rounded-trading border border-border p-6">
              <h2 className="text-xl font-heading-semibold text-text-primary mb-6">
                Withdraw Funds
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {walletData.currencies.map((currency) => (
                  <button
                    key={currency.id}
                    onClick={() => {
                      setSelectedCurrency(currency);
                      setIsWithdrawModalOpen(true);
                    }}
                    className="flex items-center justify-between p-4 border border-border rounded-trading hover:border-primary hover:bg-primary-50 transition-all duration-trading-fast"
                  >
                    <div className="flex items-center space-x-3">
                      <Image
                        src={currency.icon}
                        alt={currency.name}
                        className="w-8 h-8 rounded-full"
                      />
                      <div className="text-left">
                        <p className="font-body-medium text-text-primary">{currency.symbol}</p>
                        <p className="text-sm text-text-secondary">
                          {currency.balance.toLocaleString()} available
                        </p>
                      </div>
                    </div>
                    <Icon name="ArrowUpFromLine" size={16} className="text-text-secondary" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <TransactionHistory
              transactions={filteredTransactions}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterType={filterType}
              setFilterType={setFilterType}
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          )}

          {activeTab === 'security' && (
            <div className="bg-surface rounded-trading border border-border p-6">
              <h2 className="text-xl font-heading-semibold text-text-primary mb-6">
                Security Settings
              </h2>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 border border-border rounded-trading">
                  <div className="flex items-center space-x-3">
                    <Icon name="Shield" size={20} className="text-success" />
                    <div>
                      <p className="font-body-medium text-text-primary">Two-Factor Authentication</p>
                      <p className="text-sm text-text-secondary">Enabled via Authenticator App</p>
                    </div>
                  </div>
                  <button className="text-primary hover:text-primary-600 font-body-medium text-sm">
                    Manage
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 border border-border rounded-trading">
                  <div className="flex items-center space-x-3">
                    <Icon name="Smartphone" size={20} className="text-success" />
                    <div>
                      <p className="font-body-medium text-text-primary">SMS Verification</p>
                      <p className="text-sm text-text-secondary">+1 (555) ***-**78</p>
                    </div>
                  </div>
                  <button className="text-primary hover:text-primary-600 font-body-medium text-sm">
                    Update
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 border border-border rounded-trading">
                  <div className="flex items-center space-x-3">
                    <Icon name="Mail" size={20} className="text-success" />
                    <div>
                      <p className="font-body-medium text-text-primary">Email Notifications</p>
                      <p className="text-sm text-text-secondary">Enabled for all transactions</p>
                    </div>
                  </div>
                  <button className="text-primary hover:text-primary-600 font-body-medium text-sm">
                    Configure
                  </button>
                </div>

                <div className="p-4 bg-warning-50 border border-warning-200 rounded-trading">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                    <div>
                      <p className="font-body-medium text-warning-700 mb-1">Security Notice</p>
                      <p className="text-sm text-warning-600">
                        Withdrawals above $1,000 require 2FA confirmation. Keep your authentication methods up to date.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isDepositModalOpen && (
        <DepositModal
          currency={selectedCurrency}
          onClose={() => {
            setIsDepositModalOpen(false);
            setSelectedCurrency(null);
          }}
        />
      )}

      {isWithdrawModalOpen && (
        <WithdrawModal
          currency={selectedCurrency}
          onClose={() => {
            setIsWithdrawModalOpen(false);
            setSelectedCurrency(null);
          }}
          onWithdraw={handleWithdraw}
        />
      )}

      {isSecurityPromptOpen && (
        <SecurityPrompt
          onClose={() => {
            setIsSecurityPromptOpen(false);
            setPendingWithdrawal(null);
          }}
          onConfirm={handleSecurityConfirm}
          withdrawal={pendingWithdrawal}
        />
      )}
    </div>
  );
};

export default WalletTransactions;